// src/pages/connections/ConnectionsPage.jsx
import React, { useState, useEffect } from "react";
import {
    Container,
    Card,
    Table,
    Button,
    Form,
    Row,
    Col,
    InputGroup,
    Alert,
    Spinner,
    Modal,
} from "react-bootstrap";
import {
    FaPlus,
    FaEdit,
    FaTrash,
    FaEye,
    FaSearch,
    FaPlug,
    FaFilter,
    FaTimes,
    FaCheckCircle,
    FaClock,
    FaBan,
    FaInfoCircle,
    FaUser,
    FaPause,
    FaExchangeAlt,
    FaGasPump,
    FaShieldAlt,
    FaTools,
    FaFire,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast, Bounce } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import {
    getConnections,
    getConnectionStats,
    deleteConnection,
    activateConnection,
    suspendConnection,
    transferConnection,
    terminateConnection,
} from "../../api/tenant/connection.api";

// Custom toast helper function (matching invoice style)
const showCustomToast = (type, message, autoClose = 3000) => {
    const configs = {
        success: {
            color: "#28a745",
            border: "2px solid #28a745",
        },
        error: {
            color: "#dc3545",
            border: "2px solid #dc3545",
        },
        warning: {
            color: "#ffc107",
            border: "2px solid #ffc107",
        },
        info: {
            color: "#17a2b8",
            border: "2px solid #17a2b8",
        },
    };

    const config = configs[type];

    toast[type](message, {
        position: "top-right",
        autoClose: autoClose,
        transition: Bounce,
        style: {
            border: config.border,
            background: "transparent",
            color: config.color,
            boxShadow: "none",
            marginTop: "70px",
        },
        icon: false,
    });
};

const ConnectionsPage = () => {
    const navigate = useNavigate();

    // State Management
    const [connections, setConnections] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [gasTypeFilter, setGasTypeFilter] = useState("all");
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedConnection, setSelectedConnection] = useState(null);
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [stats, setStats] = useState(null);

    // Constants - Matching invoice STATUS_CONFIG style
    const STATUS_CONFIG = {
        active: {
            bg: "#ECFDF3",
            color: "#027A48",
            label: "Active",
            icon: <FaCheckCircle />,
        },
        pending: {
            bg: "#FEF6D7",
            color: "#FED229",
            label: "Pending",
            icon: <FaClock />,
        },
        suspended: {
            bg: "#FFE4E6",
            color: "#E11D48",
            label: "Suspended",
            icon: <FaPause />,
        },
        terminated: {
            bg: "#FFDCE2",
            color: "#F94765",
            label: "Terminated",
            icon: <FaBan />,
        },
        inactive: {
            bg: "#F1F3F5",
            color: "#6B7280",
            label: "Inactive",
            icon: <FaBan />,
        },
    };

    // Load Connections
    useEffect(() => {
        loadConnections();
        loadStats();
    }, []);

    const loadConnections = async () => {
        setLoading(true);
        setErrorMessage("");
        try {
            const result = await getConnections();
            let connectionsData = [];
            if (Array.isArray(result)) {
                connectionsData = result;
            } else if (result && result.data && Array.isArray(result.data)) {
                connectionsData = result.data;
            } else if (result && result.data && result.data.data && Array.isArray(result.data.data)) {
                connectionsData = result.data.data;
            } else {
                connectionsData = [];
            }
            setConnections(connectionsData);
        } catch (error) {
            console.error("Failed to load connections:", error);
            setErrorMessage(error.response?.data?.message || "Failed to load connections");
            showCustomToast("error", "Failed to load connections");
            setConnections([]);
        } finally {
            setLoading(false);
        }
    };

    const loadStats = async () => {
        try {
            const data = await getConnectionStats();
            setStats(data);
        } catch (error) {
            console.error("Failed to load stats:", error);
        }
    };

    // CRUD Operations
    const handleView = (connection) => {
        setSelectedConnection(connection);
        setShowViewModal(true);
        setActiveDropdown(null);
    };

    const handleEdit = (id) => {
        navigate(`/connections/edit/${id}`);
        setActiveDropdown(null);
    };

    const handleAdd = () => navigate("/connections/add");

    const handleDelete = async (id, connectionNumber) => {
        setActiveDropdown(null);
        const result = await Swal.fire({
            title: "Are you sure?",
            text: `Delete connection "${connectionNumber}"?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!",
        });

        if (result.isConfirmed) {
            try {
                await deleteConnection(id);
                showCustomToast("success", `✅ Connection "${connectionNumber}" deleted successfully!`);
                await loadConnections();
                await loadStats();
            } catch (error) {
                const errorMsg = error.response?.data?.message || "Failed to delete connection";
                if (errorMsg.includes("foreign key constraint") || errorMsg.includes("cannot delete")) {
                    showCustomToast(
                        "warning",
                        `⚠️ Cannot delete connection "${connectionNumber}" because it has associated records.`,
                        5000
                    );
                } else {
                    showCustomToast("error", `❌ ${errorMsg}`);
                }
            }
        }
    };

    const handleStatusAction = async (id, action, connectionNumber) => {
        setActiveDropdown(null);
        try {
            let successMessage;

            switch (action) {
                case "activate":
                    await activateConnection(id);
                    successMessage = `✅ Connection "${connectionNumber}" activated successfully!`;
                    break;
                case "suspend":
                    const { value: reason } = await Swal.fire({
                        title: "Enter Suspension Reason",
                        input: "text",
                        inputPlaceholder: "Reason for suspension",
                        showCancelButton: true,
                        confirmButtonText: "Suspend",
                        cancelButtonText: "Cancel",
                    });
                    if (!reason) return;
                    await suspendConnection(id, { reason });
                    successMessage = `✅ Connection "${connectionNumber}" suspended successfully!`;
                    break;
                case "transfer":
                    const { value: customerId } = await Swal.fire({
                        title: "Transfer Connection",
                        text: "Enter new customer ID:",
                        input: "text",
                        inputPlaceholder: "Customer ID",
                        showCancelButton: true,
                        confirmButtonText: "Transfer",
                        cancelButtonText: "Cancel",
                    });
                    if (!customerId) return;
                    await transferConnection(id, { customer_id: customerId });
                    successMessage = `✅ Connection "${connectionNumber}" transferred successfully!`;
                    break;
                case "terminate":
                    const result = await Swal.fire({
                        title: "Are you sure?",
                        text: `Terminate connection "${connectionNumber}"?`,
                        icon: "warning",
                        showCancelButton: true,
                        confirmButtonColor: "#d33",
                        cancelButtonColor: "#3085d6",
                        confirmButtonText: "Yes, terminate it!",
                    });
                    if (!result.isConfirmed) return;
                    await terminateConnection(id);
                    successMessage = `✅ Connection "${connectionNumber}" terminated successfully!`;
                    break;
                default:
                    return;
            }

            showCustomToast("success", successMessage);
            await loadConnections();
            await loadStats();
        } catch (error) {
            showCustomToast("error", error.response?.data?.message || `Failed to ${action} connection`);
        }
    };

    // Filtering
    const filteredConnections = connections.filter((conn) => {
        const customerName = conn.customer?.name || conn.customer_name || "";
        const customerMobile = conn.customer?.mobile || conn.customer_mobile || "";
        
        const matchesSearch =
            searchTerm === "" ||
            conn.connection_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            customerMobile.includes(searchTerm);
        const matchesStatus = statusFilter === "all" || conn.connection_status === statusFilter;
        const matchesGasType = gasTypeFilter === "all" || conn.gas_type === gasTypeFilter;
        return matchesSearch && matchesStatus && matchesGasType;
    });

    const clearFilters = () => {
        setSearchTerm("");
        setStatusFilter("all");
        setGasTypeFilter("all");
        showCustomToast("info", "Filters cleared", 2000);
    };

    // Statistics
    const statsData = stats || {
        total: connections.length,
        active: connections.filter((c) => c.connection_status === "active").length,
        suspended: connections.filter((c) => c.connection_status === "suspended").length,
        terminated: connections.filter((c) => c.connection_status === "terminated").length,
        pending: connections.filter((c) => c.connection_status === "pending").length,
    };

    // Helpers
    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString("en-IN", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount || 0);
    };

    const getStatusBadge = (status) => {
        const config = STATUS_CONFIG[status?.toLowerCase()] || STATUS_CONFIG.inactive;
        return (
            <span
                style={{
                    padding: "6px 14px",
                    borderRadius: "20px",
                    fontWeight: "600",
                    fontSize: "13px",
                    backgroundColor: config.bg,
                    color: config.color,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                }}
            >
                {config.icon} {config.label}
            </span>
        );
    };

    const getConnectionType = (connection) => {
        const types = [];
        if (connection.gas_type) types.push(connection.gas_type.toUpperCase());
        if (connection.cylinder_capacity) types.push(connection.cylinder_capacity);
        return types.join(" | ");
    };

    // Get customer name from connection (handles both nested and flat)
    const getCustomerName = (connection) => {
        return connection.customer?.name || connection.customer_name || "N/A";
    };

    // Get customer mobile from connection (handles both nested and flat)
    const getCustomerMobile = (connection) => {
        return connection.customer?.mobile || connection.customer_mobile || "";
    };

    // Get customer email from connection
    const getCustomerEmail = (connection) => {
        return connection.customer?.email || connection.customer_email || "N/A";
    };

    // Get customer address from connection
    const getCustomerAddress = (connection) => {
        return connection.customer?.address || connection.customer_address || "N/A";
    };

    // Loading State
    if (loading) {
        return (
            <Container fluid className="p-4" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
                <ToastContainer position="top-right" autoClose={3000} transition={Bounce} />
                <div className="text-center py-5">
                    <Spinner animation="border" variant="secondary" size="lg" />
                    <h4 className="mt-3">Loading connections...</h4>
                </div>
            </Container>
        );
    }

    return (
        <Container fluid className="px-4 py-3" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
            <ToastContainer position="top-right" autoClose={3000} transition={Bounce} />

            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="fw-bold mb-1" style={{ color: "rgb(30, 58, 111)" }}>
                        <FaPlug className="me-2" /> Connection Management
                    </h2>
                    <p className="text-muted">Manage all LPG connections and their status</p>
                </div>
                <Button
                    onClick={handleAdd}
                    style={{
                        backgroundColor: "rgb(30, 58, 111)",
                        border: "none",
                        borderRadius: "14px",
                        padding: "12px 22px",
                        fontWeight: "600",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                    }}
                >
                    <FaPlus size={14} /> New Connection
                </Button>
            </div>

            {/* Stats Cards */}
            <Row className="g-3 mb-4">
                <StatCard
                    title="Total Connections"
                    value={statsData.total}
                    icon={<FaPlug size={20} style={{ color: "#437EF7" }} />}
                    bg="#D3EAFF"
                />
                <StatCard
                    title="Active"
                    value={statsData.active}
                    icon={<FaCheckCircle size={20} style={{ color: "#027A48" }} />}
                    bg="#ECFDF3"
                />
                <StatCard
                    title="Suspended"
                    value={statsData.suspended}
                    icon={<FaPause size={20} style={{ color: "#E11D48" }} />}
                    bg="#FFE4E6"
                />
                <StatCard
                    title="Terminated"
                    value={statsData.terminated}
                    icon={<FaBan size={20} style={{ color: "#F94765" }} />}
                    bg="#FFDCE2"
                />
                <StatCard
                    title="Pending"
                    value={statsData.pending || 0}
                    icon={<FaClock size={20} style={{ color: "#FED229" }} />}
                    bg="#FEF6D7"
                />
            </Row>

            {/* Error Alert */}
            {errorMessage && (
                <Alert variant="danger" className="mb-4" onClose={() => setErrorMessage("")} dismissible>
                    {errorMessage}
                </Alert>
            )}

            {/* Search and Filter */}
            <Card className="border-0 shadow-sm mb-4 rounded-3">
                <Card.Body className="py-3">
                    <Row>
                        <Col md={4}>
                            <InputGroup>
                                <InputGroup.Text style={{ backgroundColor: "#fff", borderRight: "none" }}>
                                    <FaSearch className="text-muted" />
                                </InputGroup.Text>
                                <Form.Control
                                    type="text"
                                    placeholder="Search by connection no or customer..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    style={{ borderLeft: "none" }}
                                />
                                {searchTerm && (
                                    <Button variant="outline-secondary" onClick={() => setSearchTerm("")}>
                                        <FaTimes />
                                    </Button>
                                )}
                            </InputGroup>
                        </Col>
                        <Col md={3}>
                            <Form.Select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <option value="all">All Status</option>
                                <option value="active">Active</option>
                                <option value="pending">Pending</option>
                                <option value="suspended">Suspended</option>
                                <option value="terminated">Terminated</option>
                            </Form.Select>
                        </Col>
                        <Col md={3}>
                            <Form.Select
                                value={gasTypeFilter}
                                onChange={(e) => setGasTypeFilter(e.target.value)}
                            >
                                <option value="all">All Gas Types</option>
                                <option value="lpg">LPG</option>
                                <option value="png">PNG</option>
                                <option value="cng">CNG</option>
                            </Form.Select>
                        </Col>
                        <Col md={2}>
                            <Button variant="outline-secondary" onClick={clearFilters} className="w-100">
                                <FaFilter />
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Results Summary */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <p className="text-muted mb-0">
                    Showing {filteredConnections.length} of {connections.length} connection records
                </p>
            </div>

            {/* Connections Table */}
            <Card className="border-0 shadow-sm rounded-3" style={{ overflow: "hidden" }}>
                <Card.Body className="p-0">
                    <div className="table-responsive">
                        <Table hover className="mb-0" style={{ fontSize: "14px" }}>
                            <thead
                                style={{
                                    backgroundColor: "#f8f9fa",
                                    borderBottom: "2px solid #dee2e6",
                                }}
                            >
                                <tr>
                                    <th style={{ padding: "16px 12px" }}>Connection No</th>
                                    <th style={{ padding: "16px 12px" }}>Customer</th>
                                    <th style={{ padding: "16px 12px" }}>Type</th>
                                    <th style={{ padding: "16px 12px" }}>Security Deposit</th>
                                    <th style={{ padding: "16px 12px" }}>Date</th>
                                    <th style={{ padding: "16px 12px" }}>Status</th>
                                    <th style={{ padding: "16px 12px", width: "60px" }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredConnections.length > 0 ? (
                                    filteredConnections.map((connection) => (
                                        <TableRow
                                            key={connection.id}
                                            connection={connection}
                                            onView={handleView}
                                            onEdit={handleEdit}
                                            onDelete={handleDelete}
                                            onStatusAction={handleStatusAction}
                                            formatDate={formatDate}
                                            formatCurrency={formatCurrency}
                                            getStatusBadge={getStatusBadge}
                                            getConnectionType={getConnectionType}
                                            getCustomerName={getCustomerName}
                                            getCustomerMobile={getCustomerMobile}
                                            activeDropdown={activeDropdown}
                                            setActiveDropdown={setActiveDropdown}
                                        />
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="text-center py-5">
                                            <div className="py-4">
                                                <FaPlug size={50} className="text-muted mb-3" />
                                                <h5 className="text-muted">No connections found</h5>
                                                <p className="text-muted small">
                                                    Click 'New Connection' to add your first connection
                                                </p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                </Card.Body>
            </Card>

            {/* View Connection Modal */}
            <ConnectionViewModal
                show={showViewModal}
                onHide={() => setShowViewModal(false)}
                connection={selectedConnection}
                onEdit={handleEdit}
                formatDate={formatDate}
                formatCurrency={formatCurrency}
                getStatusBadge={getStatusBadge}
                getConnectionType={getConnectionType}
                getCustomerName={getCustomerName}
                getCustomerMobile={getCustomerMobile}
                getCustomerEmail={getCustomerEmail}
                getCustomerAddress={getCustomerAddress}
            />
        </Container>
    );
};

// ==============================================
// Stat Card Component
// ==============================================
const StatCard = ({ title, value, icon, bg }) => (
    <Col md={2.4}>
        <Card className="border-0 shadow-sm rounded-3">
            <Card.Body className="py-3 px-3">
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <small className="text-muted mb-0" style={{ fontSize: "11px" }}>
                            {title}
                        </small>
                        <h3 className="fw-bold mb-0" style={{ fontSize: "24px" }}>
                            {value}
                        </h3>
                    </div>
                    <div
                        style={{
                            backgroundColor: bg,
                            padding: "10px",
                            borderRadius: "12px",
                        }}
                    >
                        {icon}
                    </div>
                </div>
            </Card.Body>
        </Card>
    </Col>
);

// ==============================================
// Table Row Component
// ==============================================
const TableRow = ({
    connection,
    onView,
    onEdit,
    onDelete,
    onStatusAction,
    formatDate,
    formatCurrency,
    getStatusBadge,
    getConnectionType,
    getCustomerName,
    getCustomerMobile,
    activeDropdown,
    setActiveDropdown,
}) => {
    const isOpen = activeDropdown === connection.id;

    return (
        <tr style={{ borderBottom: "1px solid #e9ecef" }}>
            <td style={{ padding: "16px 12px" }}>
                <div className="fw-semibold" style={{ color: "rgb(30, 58, 111)" }}>
                    {connection.connection_number || "N/A"}
                </div>
            </td>
            <td style={{ padding: "16px 12px" }}>
                <div className="fw-semibold">{getCustomerName(connection)}</div>
                <small className="text-muted">{getCustomerMobile(connection)}</small>
            </td>
            <td style={{ padding: "16px 12px" }}>
                <small>{getConnectionType(connection)}</small>
            </td>
            <td style={{ padding: "16px 12px" }}>
                <strong style={{ color: "#027A48" }}>
                    ₹{formatCurrency(connection.security_deposit)}
                </strong>
            </td>
            <td style={{ padding: "16px 12px" }}>{formatDate(connection.connection_date)}</td>
            <td style={{ padding: "16px 12px" }}>{getStatusBadge(connection.connection_status)}</td>
            <td style={{ padding: "16px 12px" }}>
                <div className="action-dropdown">
                    <button
                        className="action-trigger"
                        onClick={() => setActiveDropdown(isOpen ? null : connection.id)}
                        style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: "2px",
                            width: "24px",
                            height: "24px",
                            borderRadius: "4px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="6" r="2" fill="currentColor" />
                            <circle cx="12" cy="12" r="2" fill="currentColor" />
                            <circle cx="12" cy="18" r="2" fill="currentColor" />
                        </svg>
                    </button>
                    {isOpen && (
                        <div className="dropdown-menu-custom">
                            <button
                                onClick={() => {
                                    setActiveDropdown(null);
                                    onView(connection);
                                }}
                                className="dropdown-item-custom"
                            >
                                <FaEye style={{ color: "#4361ee", fontSize: "12px" }} />
                            </button>
                            <button
                                onClick={() => {
                                    setActiveDropdown(null);
                                    onEdit(connection.id);
                                }}
                                className="dropdown-item-custom"
                            >
                                <FaEdit style={{ color: "#ff9800", fontSize: "12px" }} />
                            </button>
                            <button
                                onClick={() => {
                                    setActiveDropdown(null);
                                    onDelete(connection.id, connection.connection_number);
                                }}
                                className="dropdown-item-custom delete"
                            >
                                <FaTrash style={{ color: "#dc3545", fontSize: "12px" }} />
                            </button>
                            {connection.connection_status === "active" && (
                                <>
                                    <button
                                        onClick={() => {
                                            setActiveDropdown(null);
                                            onStatusAction(connection.id, "suspend", connection.connection_number);
                                        }}
                                        className="dropdown-item-custom"
                                    >
                                        <FaPause style={{ color: "#ffc107", fontSize: "12px" }} />
                                    </button>
                                    <button
                                        onClick={() => {
                                            setActiveDropdown(null);
                                            onStatusAction(connection.id, "transfer", connection.connection_number);
                                        }}
                                        className="dropdown-item-custom"
                                    >
                                        <FaExchangeAlt style={{ color: "#17a2b8", fontSize: "12px" }} />
                                    </button>
                                    <button
                                        onClick={() => {
                                            setActiveDropdown(null);
                                            onStatusAction(connection.id, "terminate", connection.connection_number);
                                        }}
                                        className="dropdown-item-custom delete"
                                    >
                                        <FaBan style={{ color: "#dc3545", fontSize: "12px" }} />
                                    </button>
                                </>
                            )}
                            {(connection.connection_status === "suspended" || connection.connection_status === "terminated") && (
                                <button
                                    onClick={() => {
                                        setActiveDropdown(null);
                                        onStatusAction(connection.id, "activate", connection.connection_number);
                                    }}
                                    className="dropdown-item-custom"
                                >
                                    <FaCheckCircle style={{ color: "#28a745", fontSize: "12px" }} />
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </td>
        </tr>
    );
};

// ==============================================
// Connection View Modal Component
// ==============================================
// ==============================================
// Connection View Modal Component - Updated
// ==============================================
const ConnectionViewModal = ({
    show,
    onHide,
    connection,
    onEdit,
    formatDate,
    formatCurrency,
    getStatusBadge,
    getConnectionType,
}) => {
    if (!connection) return null;

    // Get customer data from connection (now includes email and address from backend)
    const customerName = connection.customer_name || "N/A";
    const customerMobile = connection.customer_mobile || "";
    const customerEmail = connection.customer_email || "N/A";
    const customerAddress = connection.customer_address || "N/A";

    return (
        <Modal show={show} onHide={onHide} size="xl" centered>
            <Modal.Header
                closeButton
                className="bg-dark text-white rounded-top-3 border-0"
            >
                <Modal.Title className="fw-bold">
                    <FaEye className="me-2" /> Connection Details
                </Modal.Title>
            </Modal.Header>
            <Modal.Body
                className="p-4"
                style={{ maxHeight: "80vh", overflowY: "auto" }}
            >
                <Row className="mb-4">
                    <Col md={6}>
                        <InfoCard title="Connection Information" icon={<FaPlug />}>
                            <InfoRow
                                label="Connection Number"
                                value={connection.connection_number || "N/A"}
                                highlight
                            />
                            <InfoRow
                                label="Connection Type"
                                value={getConnectionType(connection)}
                            />
                            <InfoRow
                                label="Connection Date"
                                value={formatDate(connection.connection_date)}
                            />
                            <InfoRow
                                label="Status"
                                value={getStatusBadge(connection.connection_status)}
                                isHtml
                            />
                        </InfoCard>
                    </Col>
                    <Col md={6}>
                        <InfoCard title="Customer Information" icon={<FaUser />}>
                            <InfoRow label="Name" value={customerName} highlight />
                            <InfoRow label="Mobile" value={customerMobile} />
                            <InfoRow label="Email" value={customerEmail} />
                            <InfoRow label="Address" value={customerAddress} />
                        </InfoCard>
                    </Col>
                </Row>

                <Row className="mb-4">
                    <Col md={6}>
                        <InfoCard title="Cylinder Details" icon={<FaGasPump />}>
                            <InfoRow label="Gas Type" value={connection.gas_type?.toUpperCase() || "N/A"} />
                            <InfoRow label="Cylinder Capacity" value={connection.cylinder_capacity || "N/A"} />
                            <InfoRow label="Cylinder Type" value={connection.cylinder_type_name || "N/A"} />
                            <InfoRow label="Brand" value={connection.brand_name || "N/A"} />
                        </InfoCard>
                    </Col>
                    <Col md={6}>
                        <InfoCard title="Security & Equipment" icon={<FaShieldAlt />}>
                            <InfoRow
                                label="Security Deposit"
                                value={`₹${formatCurrency(connection.security_deposit)}`}
                                highlight
                            />
                            <InfoRow label="Regulator Number" value={connection.regulator_number || "N/A"} />
                            <InfoRow label="Regulator Type" value={connection.regulator_type || "N/A"} />
                            <InfoRow label="Stove Details" value={connection.stove_details || "N/A"} />
                        </InfoCard>
                    </Col>
                </Row>

                {connection.installation_notes && (
                    <Row className="mb-4">
                        <Col md={12}>
                            <InfoCard title="Installation Notes" icon={<FaInfoCircle />}>
                                <p className="mb-0">{connection.installation_notes}</p>
                            </InfoCard>
                        </Col>
                    </Row>
                )}
            </Modal.Body>
            <Modal.Footer className="bg-white rounded-bottom-3 border-top">
                <Button
                    variant="outline-secondary"
                    onClick={onHide}
                    style={{ borderRadius: "10px", padding: "8px 20px" }}
                >
                    Close
                </Button>
                <Button
                    variant="primary"
                    onClick={() => {
                        onHide();
                        onEdit(connection.id);
                    }}
                    style={{ borderRadius: "10px", padding: "8px 20px" }}
                >
                    <FaEdit className="me-2" />Edit
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

// ==============================================
// Helper Components
// ==============================================
const InfoCard = ({ title, icon, children }) => (
    <Card className="border-0 shadow-sm rounded-3 h-100">
        <Card.Header className="bg-white border-bottom pt-3 pb-2">
            <h6 className="fw-bold mb-0" style={{ color: "rgb(30, 58, 111)" }}>
                {icon} {title}
            </h6>
        </Card.Header>
        <Card.Body>{children}</Card.Body>
    </Card>
);

const InfoRow = ({ label, value, isHtml, highlight, success, danger }) => (
    <div
        className={`mb-2 pb-2 border-bottom ${danger ? "text-danger fw-semibold" : success ? "text-success fw-semibold" : ""}`}
    >
        <small className="text-muted">{label}</small>
        <div
            className={highlight ? "fw-bold fs-5" : ""}
            style={highlight ? { color: "rgb(30, 58, 111)" } : {}}
        >
            {isHtml ? value : <span>{value}</span>}
        </div>
    </div>
);

// Styles
const styles = document.createElement("style");
styles.textContent = `
  .rounded-3 { border-radius: 0.75rem !important; }
  .table tbody tr:hover { background-color: #f8f9fa; }
  .action-dropdown { position: relative; }
  .action-trigger { color: #64748b; transition: all 0.2s; }
  .action-trigger:hover { color: #1e293b; background-color: #f1f5f9; }
  .dropdown-menu-custom {
    position: absolute; top: 100%; right: 0; margin-top: 4px; min-width: 36px;
    background: white; border-radius: 8px; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    z-index: 1000; overflow: hidden; display: flex; flex-direction: column; gap: 2px; padding: 4px;
  }
  .dropdown-item-custom {
    display: flex; align-items: center; justify-content: center; width: 28px; height: 28px;
    padding: 0; border: none; background: white; cursor: pointer; transition: background-color 0.2s;
    border-radius: 6px; font-size: 13px;
  }
  .dropdown-item-custom:hover { background-color: #f8fafc; }
  .dropdown-item-custom.delete:hover { background-color: #fef2f2; }
  .form-control:focus, .form-select:focus { border-color: rgb(30, 58, 111); box-shadow: 0 0 0 0.2rem rgba(30, 58, 111, 0.25); }
  .btn-primary { background-color: rgb(30, 58, 111); border-color: rgb(30, 58, 111); }
  .btn-primary:hover { background-color: #1e3a6f; border-color: #1e3a6f; }

  @media (min-width: 768px) {
    .col-md-2\\.4 {
      flex: 0 0 20%;
      max-width: 20%;
    }
  }
  @media (max-width: 767px) {
    .col-md-2\\.4 {
      flex: 0 0 50%;
      max-width: 50%;
    }
  }
`;
document.head.appendChild(styles);

export default ConnectionsPage;